import { useEffect, useRef, useState } from "react";
import { FileEdit, Users, Target, Zap, type LucideIcon } from "lucide-react";

// Brand colors
const PRIMARY_BLUE = "#1356a6";
const HERO_BLUE = "#0d3b6e";

type StatItem = {
  value: string;
  label: string;
  icon: LucideIcon;
};

const stats: StatItem[] = [
  { value: "60%", label: "Reduction in charting time", icon: FileEdit },
  { value: "Lower", label: "Waiting room friction", icon: Users },
  { value: "Higher", label: "Coding accuracy", icon: Target },
  { value: "Faster", label: "Time to reimbursement", icon: Zap },
];

function AnimatedValue({ value }: { value: string }) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <span
      ref={ref}
      className={`transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
    >
      {value}
    </span>
  );
}

export default function StatsSection() {
  return (
    <section className="relative pt-20 pb-40 lg:pt-28 lg:pb-56">
      {/* Single card container with header and stats */}
      <div
        className="rounded-2xl backdrop-blur-md border border-white/30 shadow-xl overflow-hidden p-6 lg:p-8"
        style={{
          background: `linear-gradient(135deg, ${PRIMARY_BLUE} 0%, ${HERO_BLUE} 100%)`
        }}
      >
        {/* Section header - white text */}
        <div className="mb-8 animate-fade-in-up">
          <div className="text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-normal mb-4 text-white">
              Measurable Outcomes
            </h2>
            <p className="text-lg max-w-2xl mx-auto text-white">
              Early pilot results show significant improvements across key operational metrics.
            </p>
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {stats.map((stat, idx) => {
            const Icon = stat.icon;
            return (
              <div
                key={stat.label}
                className="group relative rounded-xl backdrop-blur-md border border-white/20 shadow-lg p-6 lg:p-8 text-center transition-all duration-300 hover:-translate-y-1 animate-fade-in-up bg-white/10"
                style={{ 
                  animationDelay: `${idx * 100}ms`,
                }}
              >
                {/* Icon */}
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-white/10 flex items-center justify-center">
                  <Icon className="w-6 h-6" style={{ color: "#0AB89E" }} />
                </div>

                {/* Value */}
                <div className="text-3xl sm:text-4xl lg:text-5xl font-normal mb-2 text-white">
                  <AnimatedValue value={stat.value} />
                </div>

                {/* Label */}
                <div className="text-sm sm:text-base text-white font-medium">{stat.label}</div>
              </div>
            );
          })}
        </div>

        {/* Disclaimer */}
        <p className="text-center text-white/50 text-sm mt-8">
          *Early pilot estimates; actual results may vary.
        </p>
      </div>
    </section>
  );
}
